// ---------------------------------------------------------------------------
// field-values.ts -- Server-resolved field-value operations for set()/setMany().
//
// A field in a write payload may be a plain value OR an "operation object" that
// the server resolves atomically when the write commits:
//
//   set("counters/likes", { n: increment(1) })          // atomic server-side +1
//   set("posts/p1", { createdAt: serverTimestamp() })   // server unix-seconds clock
//
// The Durable Object serializes writes per room, so concurrent increments never
// lose updates (verified: 20 concurrent +1 -> exactly 20). These helpers just
// construct the operation objects the worker's resolveOperationFields understands
// (and which the optimistic store mirrors locally), so prefer them over writing
// `{ operation: "increment", value: 1 }` by hand.
// ---------------------------------------------------------------------------
/**
 * Atomically add `value` to a numeric field when the write commits, server-side.
 * Use a negative `value` to decrement. The field starts from 0 if the document
 * (or field) does not exist yet. Concurrent increments are race-free.
 *
 *   await set("counters/likes", { n: increment(1) });
 *   await set("scores/p1", { points: increment(-5) });
 */
export function increment(value) {
    return { operation: 'increment', value };
}
/**
 * Stamp a field with the server's clock (Unix seconds) when the write commits —
 * the trustworthy "when did this happen" you can't get from a client clock.
 *
 *   await set("posts/p1", { createdAt: serverTimestamp() });
 */
export function serverTimestamp() {
    return { operation: 'time', value: 'now' };
}
